import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { createInvestorAnnouncement, getInvestorAnnouncements, type InvestorAnnouncementRecord } from '../../utils/supabase';

const Page = styled.div`
  padding: 24px 20px 100px;
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const Title = styled.h3`
  font-size: 16px;
  font-weight: 700;
  color: #ffffff;
`;

const Card = styled.div`
  border: 1px solid #333333;
  border-radius: 16px;
  padding: 16px;
  background: #000000;
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const Label = styled.label`
  font-size: 12px;
  color: #a1a1aa;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const Input = styled.input`
  background: #000000;
  border: 1px solid #333333;
  border-radius: 12px;
  padding: 12px 14px;
  color: #ffffff;
  font-size: 14px;
`;

const TextArea = styled.textarea`
  min-height: 120px;
  resize: vertical;
  background: #000000;
  border: 1px solid #333333;
  border-radius: 12px;
  padding: 12px;
  color: #ffffff;
  font-size: 14px;
`;

const FooterRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
`;

const Count = styled.div<{ $overLimit?: boolean }>`
  font-size: 12px;
  color: ${props => props.$overLimit ? '#ef4444' : '#71717a'};
`;

const Button = styled.button`
  padding: 10px 16px;
  border-radius: 12px;
  background: linear-gradient(135deg, #10b981 0%, #059669 100%);
  color: #ffffff;
  font-weight: 700;
  font-size: 13px;
  border: none;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: linear-gradient(135deg, #34d399 0%, #10b981 100%);
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const Message = styled.div<{ $success?: boolean }>`
  font-size: 13px;
  color: ${props => props.$success ? '#10b981' : '#ef4444'};
`;

const AnnouncementItem = styled.div`
  border-bottom: 1px solid #222222;
  padding-bottom: 12px;
  display: flex;
  flex-direction: column;
  gap: 6px;

  &:last-child {
    border-bottom: none;
    padding-bottom: 0;
  }
`;

const AnnouncementMeta = styled.div`
  font-size: 11px;
  color: #71717a;
  text-transform: uppercase;
  letter-spacing: 0.4px;
`;

const AnnouncementText = styled.div`
  font-size: 14px;
  color: #ffffff;
  line-height: 1.5;
`;

const EmptyState = styled.div`
  font-size: 13px;
  color: #71717a;
`;

const InvestorAdminAnnouncementsPWA: React.FC = () => {
  const [message, setMessage] = useState('');
  const [targetName, setTargetName] = useState('');
  const [status, setStatus] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [announcements, setAnnouncements] = useState<InvestorAnnouncementRecord[]>([]);
  const [loading, setLoading] = useState(false);

  const maxLength = 250;
  const remaining = maxLength - message.length;
  const overLimit = remaining < 0;

  const loadAnnouncements = async () => {
    setLoading(true);
    const result = await getInvestorAnnouncements('');
    if (result.success) {
      setAnnouncements(result.announcements || []);
    }
    setLoading(false);
  };

  useEffect(() => {
    loadAnnouncements();
  }, []);

  const handleSubmit = async () => {
    setStatus(null);
    if (!message.trim()) {
      setStatus({ type: 'error', text: 'Please enter an announcement.' });
      return;
    }
    if (overLimit) {
      setStatus({ type: 'error', text: 'Announcement exceeds 250 characters.' });
      return;
    }

    setSubmitting(true);
    const result = await createInvestorAnnouncement(message, targetName);
    setSubmitting(false);

    if (!result.success) {
      setStatus({ type: 'error', text: result.error || 'Failed to create announcement.' });
      return;
    }

    setMessage('');
    setTargetName('');
    setStatus({ type: 'success', text: 'Announcement sent.' });
    loadAnnouncements();
  };

  return (
    <Page>
      <Title>Announcements</Title>
      <Card>
        <Label htmlFor="announcement-target">Send To (optional)</Label>
        <Input
          id="announcement-target"
          placeholder="Leave blank for all investors"
          value={targetName}
          onChange={(e) => setTargetName(e.target.value)}
        />
        <Label htmlFor="announcement-message">Message</Label>
        <TextArea
          id="announcement-message"
          placeholder="Write an announcement..."
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          maxLength={maxLength + 5}
        />
        <FooterRow>
          <Count $overLimit={overLimit}>{remaining} characters remaining</Count>
          <Button onClick={handleSubmit} disabled={submitting || !message.trim()}>
            {submitting ? 'Sending...' : 'Send'}
          </Button>
        </FooterRow>
        {status && <Message $success={status.type === 'success'}>{status.text}</Message>}
      </Card>
      <Card>
        <Label>Recent Announcements</Label>
        {loading && <EmptyState>Loading...</EmptyState>}
        {!loading && announcements.length === 0 && <EmptyState>No announcements yet.</EmptyState>}
        {!loading && announcements.length > 0 && (
          <>
            {announcements.map((announcement) => (
              <AnnouncementItem key={announcement.id || announcement.created_at}>
                <AnnouncementMeta>
                  {announcement.investor_name ? `Direct • ${announcement.investor_name}` : 'All Investors'}
                  {announcement.created_at ? ` • ${new Date(announcement.created_at).toLocaleString()}` : ''}
                </AnnouncementMeta>
                <AnnouncementText>{announcement.message}</AnnouncementText>
              </AnnouncementItem>
            ))}
          </>
        )}
      </Card>
    </Page>
  );
};

export default InvestorAdminAnnouncementsPWA;
